import React from "react";
import { Box, Typography, ButtonBase } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HelpIcon from "@mui/icons-material/Help";
import { useTranslation } from "../../hooks/useTranslation";

interface FaultStepProps {
  accidentFault: string;
  onChange: (field: string, value: string) => void;
  setDequalified: () => void;
}

const FaultStep: React.FC<FaultStepProps> = ({
  accidentFault,
  onChange,
  setDequalified,
}) => {
  const { t } = useTranslation();

  const options = [
    {
      value: "NO",
      label: t("form.thirdQuestion.option1"),
      icon: CheckCircleIcon,
      iconColor: "#22c55e",
      selectedBg: "#dcfce7",
      selectedBorder: "#22c55e",
      hoverBg: "#dcfce7",
      onClick: () => onChange("accidentFault", "NO"),
    },
    {
      value: "YES",
      label: t("form.thirdQuestion.option2"),
      icon: CancelIcon,
      iconColor: "#ef4444",
      selectedBg: "#fee2e2",
      selectedBorder: "#ef4444",
      hoverBg: "#fee2e2",
      onClick: () => onChange("accidentFault", "YES"),
    },
    {
      value: "DEQUALIFIED",
      label: t("form.thirdQuestion.option3"),
      icon: HelpIcon,
      iconColor: "#f59e0b",
      selectedBg: "#fef3c7",
      selectedBorder: "#f59e0b",
      hoverBg: "#fef3c7",
      onClick: setDequalified,
    },
  ];

  return (
    <Box sx={{ py: 2 }}>
      {/* Question */}
      <Typography
        variant="h5"
        align="center"
        sx={{
          mb: 4,
          fontWeight: 500,
          color: "#374151",
          fontSize: { xs: "1.1rem", sm: "1.5rem" },
        }}
      >
        {t("form.thirdQuestion.question")}
      </Typography>

      {/* Icon Buttons */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "flex-start",
          gap: { xs: 1.5, sm: 4 },
          flexWrap: "nowrap",
        }}
      >
        {options.map((option) => {
          const IconComponent = option.icon;
          const isSelected = accidentFault === option.value;

          return (
            <ButtonBase
              key={option.value}
              onClick={option.onClick}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
                transition: "transform 0.2s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
            >
              <Box
                sx={{
                  width: { xs: 80, sm: 120 },
                  height: { xs: 80, sm: 120 },
                  borderRadius: "50%",
                  backgroundColor: isSelected ? option.selectedBg : "#e5e7eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s ease",
                  border: isSelected
                    ? `3px solid ${option.selectedBorder}`
                    : "3px solid transparent",
                  "&:hover": {
                    backgroundColor: option.hoverBg,
                  },
                }}
              >
                <IconComponent
                  sx={{
                    fontSize: { xs: 40, sm: 60 },
                    color: option.iconColor,
                  }}
                />
              </Box>
              <Typography
                sx={{
                  color: "#0ea5e9",
                  fontWeight: 500,
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  textDecoration: "underline",
                  textUnderlineOffset: "4px",
                  textAlign: "center",
                  maxWidth: { xs: 80, sm: 120 },
                }}
              >
                {option.label}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
};

export default FaultStep;
